import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Power, Trash2, LogOut, Store, Users, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

export default function SuperAdminDashboard() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', whatsapp: '', address: '', owner_username: '', owner_password: '' });
  const [saving, setSaving] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get('/api/superadmin/restaurants');
      setRestaurants(res.data);
    } catch { toast.error('Failed to load restaurants'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.owner_username || !form.owner_password)
      return toast.error('Please fill all required fields');
    setSaving(true);
    try {
      await api.post('/api/superadmin/restaurants', form);
      toast.success('Restaurant created!');
      setForm({ name: '', whatsapp: '', address: '', owner_username: '', owner_password: '' });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/api/superadmin/restaurants/${id}/toggle`);
      toast.success('Status updated');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its data?`)) return;
    try {
      await api.delete(`/api/superadmin/restaurants/${id}`);
      toast.success('Restaurant deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const active = restaurants.filter(r => r.is_active).length;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>🍽️ Super Admin</h1>
          <p style={styles.headerSub}>QR Menu Platform</p>
        </div>
        <button onClick={() => { logout(); navigate('/login'); }} style={styles.logoutBtn}>
          <LogOut size={18} />
        </button>
      </header>

      <div style={styles.content}>
        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <Store size={24} color="#FF6B35" />
            <span style={styles.statNum}>{restaurants.length}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={styles.statCard}>
            <CheckCircle size={24} color="#27AE60" />
            <span style={styles.statNum}>{active}</span>
            <span style={styles.statLabel}>Active</span>
          </div>
          <div style={styles.statCard}>
            <XCircle size={24} color="#E74C3C" />
            <span style={styles.statNum}>{restaurants.length - active}</span>
            <span style={styles.statLabel}>Inactive</span>
          </div>
        </div>

        {/* Add Button */}
        <button className="btn btn-primary btn-full" onClick={() => setShowForm(!showForm)} style={{ marginBottom: 16 }}>
          <Plus size={18} /> Add New Restaurant
        </button>

        {/* Add Form */}
        {showForm && (
          <div className="card fade-in" style={{ marginBottom: 16 }}>
            <h3 style={styles.formTitle}>New Restaurant</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="label">Restaurant Name *</label>
                <input className="input" placeholder="e.g. Sharma Dhaba" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="label">WhatsApp Number *</label>
                <input className="input" placeholder="91XXXXXXXXXX" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="label">Address</label>
                <input className="input" placeholder="Restaurant address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="label">Owner Username *</label>
                <input className="input" placeholder="owner_username" value={form.owner_username} onChange={e => setForm({ ...form, owner_username: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="label">Owner Password *</label>
                <input className="input" type="password" placeholder="Set a password" value={form.owner_password} onChange={e => setForm({ ...form, owner_password: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving} style={{ flex: 1 }}>
                  {saving ? <span className="spinner" style={{ width: 18, height: 18 }} /> : 'Create'}
                </button>
                <button className="btn btn-secondary" type="button" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Restaurant List */}
        <h2 style={styles.sectionTitle}>Restaurants</h2>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : restaurants.length === 0 ? (
          <div className="empty-state">
            <Store size={40} />
            <h3>No restaurants yet</h3>
            <p>Add your first restaurant above</p>
          </div>
        ) : (
          restaurants.map(r => (
            <div key={r.id} className="card fade-in" style={styles.restaurantCard}>
              <div style={styles.restaurantInfo}>
                <div style={styles.restaurantIcon}>
                  {r.logo ? <img src={r.logo} alt="" style={styles.logoImg} /> : '🍽️'}
                </div>
                <div>
                  <h3 style={styles.restaurantName}>{r.name}</h3>
                  <p style={styles.restaurantSub}>📱 {r.whatsapp}</p>
                  {r.address && <p style={styles.restaurantSub}>📍 {r.address}</p>}
                </div>
              </div>
              <div style={styles.restaurantActions}>
                <span className={`badge ${r.is_active ? 'badge-success' : 'badge-danger'}`}>
                  {r.is_active ? '● Active' : '● Inactive'}
                </span>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className={`btn btn-sm ${r.is_active ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => handleToggle(r.id)}
                  >
                    <Power size={14} />
                    {r.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r.id, r.name)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#FAFAFA' },
  header: {
    background: 'white',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #ECF0F1',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#2C3E50' },
  headerSub: { fontSize: '12px', color: '#7F8C8D' },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#7F8C8D', padding: 8 },
  content: { padding: '16px', maxWidth: '600px', margin: '0 auto' },
  statsRow: { display: 'flex', gap: 12, marginBottom: 16 },
  statCard: {
    flex: 1, background: 'white', borderRadius: 12, padding: '16px 12px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },
  statNum: { fontSize: '24px', fontWeight: '700', color: '#2C3E50' },
  statLabel: { fontSize: '11px', color: '#7F8C8D' },
  formTitle: { fontSize: '16px', fontWeight: '600', marginBottom: 16, color: '#2C3E50' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: 12 },
  restaurantCard: { marginBottom: 12 },
  restaurantInfo: { display: 'flex', gap: 12, marginBottom: 12 },
  restaurantIcon: { fontSize: '32px', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff0eb', borderRadius: 10 },
  logoImg: { width: 48, height: 48, borderRadius: 10, objectFit: 'cover' },
  restaurantName: { fontSize: '15px', fontWeight: '600', color: '#2C3E50', marginBottom: 2 },
  restaurantSub: { fontSize: '12px', color: '#7F8C8D', marginBottom: 1 },
  restaurantActions: {},
};
